import React, { useState, useEffect } from 'react';
import { MethodProps } from '../types';
import styled from '@emotion/styled';
import NumberInput from '~ComponentsImproved/Numpad';
import { useButton, useCurrency } from '~Hooks';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { useCashApi } from '~Components/CashUniverse';
import { useI18n, useDispatch } from '~Hooks';
import { StyledH1, StyledH2 } from '~ComponentsImproved/Typography/Heading/styles';
import { BlueButton, Box, Button } from '~Styles';
import { addToast } from '~actions';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import { useStorage } from '~Components/Storage';
import { handyBreakpoint } from '~Styles/breakpoints';
import DividerWithText from '~Components/DividerWithText';

const ContentWrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  padding: 24px 80px;
  min-height: 200px;
  max-width: 600px;
  min-width: 400px;

  ${handyBreakpoint} {
    min-width: 'auto';
    max-width: 'auto';
    min-height: 'auto';

    padding: 16px 32px;
  }
`;

const Title = styled(StyledH1)`
  text-align: center;

  ${handyBreakpoint} {
    font-size: 24px;
  }
`;

const CashLeftover = styled(StyledH2)`
  text-align: center;
  font-size: 64px;
  color: var(--colorUIGreen);

  ${handyBreakpoint} {
    font-size: 48px;
  }
`;

const TipsButton = styled(Button)`
  min-height: 32px;
  padding: 24px;

  ${handyBreakpoint} {
    font-size: 12px;
    min-height: 32px;
  }
`;

const ChangeButton = styled(BlueButton)`
  min-height: 32px;
  padding: 24px;

  ${handyBreakpoint} {
    font-size: 12px;
    min-height: 32px;
  }
`;

/**
 * Cash payment method component
 */
const Cash: React.FC<MethodProps> = ({
  amount,
  discount,
  onComplete,
  onFail,
  onCancel,
  onMetaChange,
  payment,
}) => {
  const { i18n } = useI18n();

  const isHandy = location.pathname.includes('handy');

  const cashDrawer = useCashDrawer();
  const cashInfoModal = useModal();
  const dispatch = useDispatch();
  const currency = useCurrency();
  const cashApi = useCashApi();

  const [storage] = useStorage();

  const [numpadOpen, setNumpadOpen] = useState<boolean>(true);
  const [manualTipsNumpadOpen, setManualTipsNumpadOpen] = useState<boolean>(false);
  const [manualTipsEntered, setManualTipsEntered] = useState<number | null>(null);

  const [cashHanded, setCashHanded] = useState<number | null>(null);
  const [cashLeftover, setCashLeftover] = useState<number>(0); // How much cash is left after the payment is completed

  const manualTipsButton = useButton({
    onClick: () => {
      setManualTipsNumpadOpen(true);
    },
  });

  const fullTipsButton = useButton({
    onClick: () => {
      onSubmit(cashLeftover);
    },
  });

  const changeButton = useButton({
    onClick: () => {
      onSubmit();
    },
  });

  const [error, setError] = useState<boolean>(false);

  // If the user cancels the number input for manual tips, the numpad is closed
  const cancelManualTips = () => {
    setManualTipsNumpadOpen(false);
  };

  const confirmManualTips = (digits: string) => {
    const cashTips = digits ? Number(parseFloat(digits).toFixed(2)) * 100 : cashLeftover;

    if (cashTips) {
      setManualTipsEntered(cashTips);
      setCashLeftover(cashLeftover - cashTips);
    }

    setManualTipsNumpadOpen(false);

    const cashLeftoverAfterTips = cashLeftover - cashTips;

    /* We can just close the modal directly now since there is no cash left to give the customer */
    if (cashLeftoverAfterTips === 0) {
      /* We can send in cashTips as param allLeftoverAsTips since the cashier entered just that */
      onSubmit(cashTips);
    }
  };

  // If the user presses outside of the numpad, the payment is canceled
  const cancelPayment = () => {
    setNumpadOpen(false);

    cashInfoModal.toggle();

    onFail(i18n('Cash.Canceled'));
  };

  const confirmAmount = (digits: string) => {
    if (!amount) {
      return;
    }

    const cash = digits ? Number(parseFloat(digits).toFixed(2)) * 100 : amount;

    if (cash < amount) {
      dispatch(addToast(i18n('Cash.ReceivedLessThanTotal'), true));
      return;
    }

    setCashHanded(cash);
    setCashLeftover(cash - amount);

    cashDrawer.open();

    if (cash > amount) {
      cashInfoModal.toggle();
    } else {
      onSubmit();
    }
  };

  const onSubmit = (allLeftoverAsTips?: number) => {
    if (cashInfoModal.isShowing) {
      cashInfoModal.toggle();
    }

    /* 
    Three outcomes with tips:
     (1) We have tips in the parameter, which means "Log all as tips" was pressed
     (2) User entered a tip amount manually (manualTipsEntered) 
     (3) No tips at all, the user has pressed "Give as change"
     */
    const tipsAmount = allLeftoverAsTips ?? manualTipsEntered ?? 0;

    cashApi.updateCash(amount! + tipsAmount);
    onComplete(payment.id, { tip: tipsAmount });
  };

  useEffect(() => {
    // This variable is used to ensure that it doesn't set error incorrectly on 0 due to floating-point precision issues
    const roundedCashLeftover = Math.round(cashLeftover * 100) / 100;
    setError(roundedCashLeftover < 0);
  }, [cashLeftover]);

  // Ensures that very small negative values due to floating-point precision issues are displayed as "0.00"
  const displayCashLeftover =
    cashLeftover < 0 && Math.abs(cashLeftover) < 0.01
      ? currency.format(0)
      : currency.format(cashLeftover);

  return (
    <>
      <Modal isShowing={cashInfoModal.isShowing} hide={cancelPayment} noPadding>
        <ContentWrapper display="flex" flexDirection="column" justifyContent="space-between">
          <Title color="var(--colorWhite)">
            {storage.enableTipping ? i18n('Cash.Payment') : i18n('Cash.ChangeBack')}
          </Title>

          <CashLeftover>{displayCashLeftover}</CashLeftover>

          <Box
            display="flex"
            flexDirection="column"
            justifyContent={storage.enableTipping ? 'space-between' : 'center'}
            gap={isHandy ? 'small' : 'medium'}
            marginTop={isHandy ? 'none' : 'xl'}
            marginBottom={isHandy ? 'none' : 'medium'}>
            {storage.enableTipping && !manualTipsEntered && (
              <>
                <Box display="flex" justifyContent="space-between" gap="medium">
                  <TipsButton {...manualTipsButton}>{i18n('Cash.LogManualTips')}</TipsButton>
                  <TipsButton {...fullTipsButton}>{i18n('Cash.LogAllAsTips')}</TipsButton>
                </Box>

                <DividerWithText />
              </>
            )}

            <ChangeButton {...changeButton} disabled={error}>
              {storage.enableTipping ? i18n('Cash.GiveAsChange') : i18n('Cash.CompletePayment')}
            </ChangeButton>
          </Box>
        </ContentWrapper>
      </Modal>

      {numpadOpen && !cashHanded && (
        <NumberInput
          title={i18n('Cash.TotalCashReceived')}
          initialValueIsPlaceholder
          initialValue={((amount ?? 0) / 100).toString()}
          onClose={cancelPayment}
          onSubmit={confirmAmount}
          decimal
        />
      )}

      {manualTipsNumpadOpen && (
        <NumberInput
          title={i18n('Cash.Manualtips', displayCashLeftover)}
          initialValueIsPlaceholder
          initialValue={
            cashLeftover < 0 && Math.abs(cashLeftover) < 0.01
              ? '0'
              : (cashLeftover / 100).toString()
          } // Ensures that very small negative values due to floating-point precision issues are displayed as "0.00"
          maxValue={cashLeftover / 100}
          onClose={cancelManualTips}
          onSubmit={confirmManualTips}
          decimal
        />
      )}
    </>
  );
};

export default Cash;
